
import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  CancelButton,
  SaveButton
} from './styles';

import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../store';
import {
  createCancellationReason,
  updateCancellationReason
} from '../../../../../modules/masterdata/actions';
import {
  selectCreateCancellationReasonSuccess,
  selectUpdateCancellationReasonSuccess,
  selectCreateCancellationReasonLoading,
  selectUpdateCancellationReasonLoading
} from '../../../../../modules/masterdata/selectors';

import { ICancellationReason } from '../../../../../modules/masterdata/types';

interface CancellationReasonModalProps {
  open: boolean;
  onClose: () => void;
  reason?: ICancellationReason | null; // If provided, we are in Edit mode
}

const CancellationReasonModal: React.FC<CancellationReasonModalProps> = ({ open, onClose, reason }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const createSuccess = useSelector(selectCreateCancellationReasonSuccess);
  const updateSuccess = useSelector(selectUpdateCancellationReasonSuccess);
  const createLoading = useSelector(selectCreateCancellationReasonLoading);
  const updateLoading = useSelector(selectUpdateCancellationReasonLoading);

  const isLoading = createLoading || updateLoading;

  useEffect(() => {
    if (reason) {
      setName(reason.reason);
      setDescription(reason.reason_description);
    } else {
      setName('');
      setDescription('');
    }
  }, [reason, open]);

  /* Removed internal alert state and logic to match RoleModal behavior */

  useEffect(() => {
    if (createSuccess) {
      onClose();
    }
    if (updateSuccess) {
      onClose();
    }
  }, [createSuccess, updateSuccess, onClose]);

  const [nameError, setNameError] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);

  const handleSave = () => {
    const isNameValid = !!name.trim();
    const isDescriptionValid = !!description.trim();

    setNameError(!isNameValid);
    setDescriptionError(!isDescriptionValid);

    if (!isNameValid || !isDescriptionValid) {
      return;
    }

    if (reason) {
      dispatch(updateCancellationReason({
        id: reason.reason_id,
        reason: name,
        reason_description: description
      }));
    } else {
      dispatch(createCancellationReason({
        reason: name,
        reason_description: description
      }));
    }
  };

  return (
    <StyledDialog open={open} onClose={onClose}>
      <StyledDialogTitle>
        {reason ? 'Edit Cancellation Reason' : 'Create Cancellation Reason'}
      </StyledDialogTitle>
      <StyledDialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          required
          fullWidth
          value={name}
          error={nameError}
          helperText={nameError ? 'Name is required' : ''}
          onChange={(e) => {
            setName(e.target.value);
            if (e.target.value.trim()) setNameError(false);
          }}
        />
        <TextField
          margin="dense"
          label="Description"
          required
          fullWidth
          multiline
          rows={3}
          value={description}
          error={descriptionError}
          helperText={descriptionError ? 'Description is required' : ''}
          onChange={(e) => {
            setDescription(e.target.value);
            if (e.target.value.trim()) setDescriptionError(false);
          }}
        />
      </StyledDialogContent>
      <StyledDialogActions>
        <CancelButton onClick={onClose}>
          Cancel
        </CancelButton>
        <SaveButton onClick={handleSave} variant="contained" disableElevation disabled={isLoading}>
          {isLoading ? 'Saving...' : (reason ? 'Save Changes' : 'Create')}
        </SaveButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default CancellationReasonModal;
